import { Button, Popover, Tooltip, useToast } from "@hellocarbo/lasagne";
import { cn } from "@hellocarbo/lasagne/utils";
import { ArrowRight, Ellipsis, PowerOffIcon, TrashIcon } from "lucide-react";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFetcher, useParams } from "react-router";
import invariant from "tiny-invariant";

import { PortalContainerContext } from "@/contexts";
type EditActionsProps = {
  importDepositId: number;
  importDepositStatus: string;
  isScoreReadOnly: boolean;
  detailUrl: string;
};

const EditActions = ({
  importDepositId,
  importDepositStatus,
  isScoreReadOnly,
  detailUrl,
}: EditActionsProps) => {
  const { t } = useTranslation("importDeposit");
  const { scoreId } = useParams();
  const { container } = useContext(PortalContainerContext);
  const fetcher = useFetcher();
  const { toast } = useToast();

  invariant(scoreId, "No scoreId found!");

  const handleCancelClick = () => {
    const data = new FormData();
    data.set("scoreId", scoreId);
    data.set("importId", importDepositId.toString());
    data.set("actionType", "cancel");
    fetcher.submit(data, {
      method: "POST",
    });
  };

  const handleDeleteRelatedSurveysClick = () => {
    const data = new FormData();
    data.set("scoreId", scoreId);
    data.set("importId", importDepositId.toString());
    data.set("actionType", "deleteRelatedSurveys");
    fetcher.submit(data, {
      method: "DELETE",
    });
  };

  const handleDeleteClick = () => {
    const data = new FormData();
    data.set("scoreId", scoreId);
    data.set("importId", importDepositId.toString());
    data.set("actionType", "delete");
    fetcher.submit(data, {
      method: "DELETE",
    });
  };

  useEffect(() => {
    if (
      fetcher.state === "idle" &&
      fetcher.data?.ok &&
      fetcher.data?.actionType === "delete"
    ) {
      toast({
        title: t("importDeposit:importAction.deleteSuccessToast"),
        intent: "positive",
      });
    }
  }, [fetcher.state, fetcher.data, toast, t]);

  // Delete all survey forms that were created by the `integrated` import
  // Sets the import_deposit status to `cancelled`
  const canDeleteRelatedSurveys = importDepositStatus === "integrated";
  // Cancel a non integrated import (survey forms have not been created yet)
  // Sets the import_deposit status to `cancelled`
  const canBeCanceled = ["matching", "accepted"].includes(importDepositStatus);
  // Delete the actual import_deposit
  const canBeDeleted = ["refused", "cancelled"].includes(importDepositStatus);

  return (
    <td className={"py-sm grid grid-cols-2 items-center"}>
      {!isScoreReadOnly && (
        <div className="w-full">
          <Popover
            align="center"
            side="left"
            sideOffset={4}
            portalContainer={container as Element}
            trigger={<Button Icon={Ellipsis} intent="primary" shape="link" />}
          >
            {canDeleteRelatedSurveys && (
              <Button
                shape="link"
                intent="negative"
                onClick={handleDeleteRelatedSurveysClick}
                disabled={fetcher.state !== "idle"}
              >
                <TrashIcon />
                {t(`importDeposit:importAction.deleteRelatedSurveysCta`)}
              </Button>
            )}
            {canBeCanceled && (
              <Button
                shape="link"
                intent="negative"
                onClick={handleCancelClick}
                disabled={fetcher.state !== "idle"}
              >
                <PowerOffIcon />
                {t(`importDeposit:importAction.cancelCta`)}
              </Button>
            )}
            {canBeDeleted && (
              <Button
                shape="link"
                intent="negative"
                onClick={handleDeleteClick}
                disabled={fetcher.state !== "idle"}
              >
                <TrashIcon />
                {t(`importDeposit:importAction.deleteCta`)}
              </Button>
            )}
          </Popover>
        </div>
      )}

      <div className={cn("w-full", isScoreReadOnly && "col-start-2")}>
        <Tooltip
          content={t(`importDeposit:importAction.seeMoreCta`)}
          side="left"
          portalContainer={container as Element}
        >
          <Button
            as="a"
            href={detailUrl}
            Icon={ArrowRight}
            intent="primary"
            shape="soft"
          />
        </Tooltip>
      </div>
    </td>
  );
};

export default EditActions;
